import React, { useCallback, useEffect, useRef } from 'react';
import { useModalActions } from '../../layout/Modals/modalActions';
import { useCreateResource__ALPHA } from '@cube3/state/src/redux/components/Hooks/useCreateResource';
import { useVisitingFolder } from '@cube3/state/src/redux/components/Hooks/clipboard/useVisitingFolder';
import { uuidv4 } from '@cube3/state/src/utils/uuid';
import { getEnvVar } from '../../../../utils/getEnvVar';

export type DropboxChooserFile = {
    /** Unique ID for the file, compatible with Dropbox API v2. */
    id: string;
    /** Name of the file */
    name: string;
    /** Size of the file in bytes. */
    bytes: number;
    /** URL to a 64x64px icon for the file based on the file's extension. */
    icon: string;
    /** URL to access the file, which varies depending on the linkType specified when the Chooser was triggered. */
    link: string;
    /** Whether or not the file is actually a directory */
    isDir: boolean;
    /** A thumbnail URL generated when the user selects images and videos.
     * - If the user didn't select an image or video, no thumbnail will be included. */
    thumbnailLink?: string;
};



export const DropboxChooser = () => {

    const DROPBOX_IFRAME_ORIGIN = `${location.protocol}//${getEnvVar("DROPBOX_WRAPPER_HOST") || "xtrn.localhost:9000"}`;
    const DROPBOX_WRAPPER_URL = `${DROPBOX_IFRAME_ORIGIN}/dropbox.html`

    const { closeAllModals } = useModalActions();
    const [visitingFolderId] = useVisitingFolder({
        getAncestors: false
    });

    const iframeRef = useRef<HTMLIFrameElement>(null);

    const [createResource] = useCreateResource__ALPHA({
        resourceType: 'asset',
        cacheInvalidator: useCallback(
            () => visitingFolderId ? [
                {
                    type: 'folder' as const,
                    id: visitingFolderId,
                    relationship: 'assets'
                }
            ] : [],
            [visitingFolderId]
        )
    });

    const onCreateAssets = useCallback(
        (files: DropboxChooserFile[]) => {
            const withTempId = files.map((file) => {
                const temporaryId = uuidv4();
                return {
                    ...file,
                    temporaryId,
                    display_image: file.thumbnailLink,
                    display_name: file.name
                };
            });
            const resources = withTempId.map((file) => {
                return {
                    type: 'asset' as const,
                    temporaryId: file.temporaryId,
                    display_name: file.name,
                    source_url: file.link,
                    relationships: {
                        parent: {
                            data: {
                                type: 'node',
                                id: visitingFolderId
                            }
                        }
                    }
                };
            });

            createResource(resources);
        },
        [visitingFolderId, createResource]
    );

    // Receive messages from the Dropbox iframe
    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {

            console.info("DEBUG event origin from frame", { or: event.origin, ex: DROPBOX_IFRAME_ORIGIN, m: event.origin === DROPBOX_IFRAME_ORIGIN, d: event.data })

            if (event.origin !== DROPBOX_IFRAME_ORIGIN) {
                return;
            }
            const { type } = event.data ?? {};
            switch (type) {
                case 'DROPBOX_FILES_SELECTED': {
                    const files = event.data.files as DropboxChooserFile[];
                    console.debug('Dropbox files selected:', files);
                    onCreateAssets(files);
                    closeAllModals();
                    break;
                }

                case 'DROPBOX_CANCELLED': {
                    console.debug('Dropbox chooser cancelled');
                    closeAllModals();
                    break;
                }

                default:
                    break;
            }
        };
        window.addEventListener('message', handleMessage);
        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, [onCreateAssets, closeAllModals]);

    const onLoad = useCallback(() => {
        if (!iframeRef.current) return;
        iframeRef.current.contentWindow?.postMessage(
            'OPEN_CHOOSER',
            DROPBOX_IFRAME_ORIGIN
        );
    }, []);

    return (
        <iframe
            ref={iframeRef}
            src={DROPBOX_WRAPPER_URL}
            title="Dropbox integration"
            sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox allow-same-origin"
            onLoad={onLoad}
            style={{
                border: 'none',
                opacity: 0,
                pointerEvents: 'none',
                width: 1,
                height: 1
            }}
        />
    );
};
